/**
 * PilotsAdmin — list + profile editor for pilots in the selected airline.
 *
 * Covers both populations:
 *   - Registered pilots: rows in `users` with role='trainee' (or 'instructor')
 *   - Ad-hoc pilots:     rows in `org_pilots`
 *
 * Opening a pilot shows the PilotProfileEditor (identity / ratings / languages).
 * RLS enforces who can actually save.
 */

import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAirline } from '../../contexts/AirlineContext';
import PilotProfileEditor from '../../components/pilots/PilotProfileEditor';

interface PilotRow {
  id: string;
  full_name: string;
  kind: 'user' | 'org_pilot';
  email: string | null;
  airline_code: string | null;
  licence_id: string | null;
  role?: string | null;
}

type Filter = 'all' | 'registered' | 'ad_hoc';

export default function PilotsAdmin() {
  const { selectedAirline } = useAirline();
  const [rows, setRows] = useState<PilotRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [openPilot, setOpenPilot] = useState<PilotRow | null>(null);

  useEffect(() => {
    if (!selectedAirline?.airline_id) {
      setRows([]);
      setLoading(false);
      return;
    }
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      const [usersRes, pilotsRes] = await Promise.all([
        supabase
          .from('users')
          .select('id, full_name, email, licence_id, role')
          .eq('organization_id', selectedAirline!.airline_id)
          .in('role', ['trainee', 'instructor'])
          .order('full_name'),
        supabase
          .from('org_pilots')
          .select('id, full_name, email, airline_code, licence_id')
          .eq('org_id', selectedAirline!.airline_id)
          .order('full_name'),
      ]);

      if (cancelled) return;

      if (usersRes.error) {
        setError(usersRes.error.message);
        setLoading(false);
        return;
      }
      if (pilotsRes.error) {
        setError(pilotsRes.error.message);
        setLoading(false);
        return;
      }

      const combined: PilotRow[] = [
        ...(usersRes.data ?? []).map((u: any) => ({
          id: u.id,
          full_name: u.full_name ?? '',
          kind: 'user' as const,
          email: u.email ?? null,
          airline_code: null,
          licence_id: u.licence_id ?? null,
          role: u.role ?? null,
        })),
        ...(pilotsRes.data ?? []).map((p: any) => ({
          id: p.id,
          full_name: p.full_name ?? '',
          kind: 'org_pilot' as const,
          email: p.email ?? null,
          airline_code: p.airline_code ?? null,
          licence_id: p.licence_id ?? null,
          role: null,
        })),
      ];

      setRows(combined);
      setLoading(false);
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [selectedAirline?.airline_id]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter === 'registered' && r.kind !== 'user') return false;
      if (filter === 'ad_hoc' && r.kind !== 'org_pilot') return false;
      if (!q) return true;
      return (
        r.full_name.toLowerCase().includes(q) ||
        (r.email ?? '').toLowerCase().includes(q) ||
        (r.licence_id ?? '').toLowerCase().includes(q) ||
        (r.airline_code ?? '').toLowerCase().includes(q)
      );
    });
  }, [rows, search, filter]);

  if (openPilot) {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => setOpenPilot(null)}
          className="text-sm text-[#4A90E2] hover:underline"
        >
          ← Back to pilots list
        </button>
        <PilotProfileEditor
          userId={openPilot.kind === 'user' ? openPilot.id : undefined}
          orgPilotId={openPilot.kind === 'org_pilot' ? openPilot.id : undefined}
          onClose={() => setOpenPilot(null)}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row gap-2 md:items-center md:justify-between">
        <div className="flex gap-2">
          <input
            type="search"
            placeholder="Search by name, licence, email, or code…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded border border-gray-300 px-3 py-2 text-sm w-80"
          />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as Filter)}
            className="rounded border border-gray-300 px-2 py-2 text-sm"
          >
            <option value="all">All pilots</option>
            <option value="registered">Registered (users)</option>
            <option value="ad_hoc">Ad-hoc (org_pilots)</option>
          </select>
        </div>
        <div className="text-sm text-gray-500">
          {loading ? 'Loading…' : `${filtered.length} pilot${filtered.length === 1 ? '' : 's'}`}
        </div>
      </div>

      {error && (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="rounded border border-gray-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-700">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Name</th>
              <th className="px-3 py-2 text-left font-medium">Type</th>
              <th className="px-3 py-2 text-left font-medium">Role</th>
              <th className="px-3 py-2 text-left font-medium">Licence ID</th>
              <th className="px-3 py-2 text-left font-medium">Email</th>
              <th className="px-3 py-2 text-left font-medium">Pilot ID</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={`${r.kind}-${r.id}`} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-3 py-2 font-medium">{r.full_name || '—'}</td>
                <td className="px-3 py-2">
                  <span
                    className={`rounded px-2 py-0.5 text-[10px] uppercase ${
                      r.kind === 'user'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-purple-100 text-purple-800'
                    }`}
                  >
                    {r.kind === 'user' ? 'Registered' : 'Ad-hoc'}
                  </span>
                </td>
                <td className="px-3 py-2">{r.role ?? '—'}</td>
                <td className="px-3 py-2">{r.licence_id ?? '—'}</td>
                <td className="px-3 py-2">{r.email ?? '—'}</td>
                <td className="px-3 py-2">{r.airline_code ?? '—'}</td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => setOpenPilot(r)}
                    className="rounded bg-[#0B2C45] px-3 py-1 text-xs font-medium text-white hover:bg-[#0B2C45]/90"
                  >
                    Edit profile
                  </button>
                </td>
              </tr>
            ))}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-4 text-center text-gray-500">
                  No pilots match the filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
